import * as React from "react";
import { cn } from "@/lib/utils";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";

/**
 * Notice — the single source of truth for inline warnings, errors, success
 * confirmations, and info notes.
 *
 * Deliberately NOT an alert box. No tinted background, no border, no rounded
 * container: a colored translucent panel reads as generic template chrome
 * (see CLAUDE.md "Tag convention" — flagged in code review). The message
 * itself carries the signal: a small icon and plain text in the tone colour.
 * Hierarchy comes from spacing and typography, never a tinted panel.
 *
 * Multi-line content is fine — pass paragraphs, lists, or an action button
 * as children; the icon stays pinned to the first line.
 */

const TONES = {
  neutral: "text-muted-foreground",
  warning: "text-amber-600 dark:text-amber-400",
  danger: "text-red-600 dark:text-red-400",
  success: "text-emerald-600 dark:text-emerald-400",
  info: "text-sky-600 dark:text-sky-400",
} as const;

export type NoticeTone = keyof typeof TONES;

const ICONS: Record<NoticeTone, React.ComponentType<{ className?: string }>> = {
  neutral: Info,
  warning: TriangleAlert,
  danger: CircleAlert,
  success: CircleCheck,
  info: Info,
};

const SIZES = {
  sm: "text-xs gap-1.5 [&_[data-slot=notice-icon]]:size-3.5",
  md: "text-sm gap-2 [&_[data-slot=notice-icon]]:size-4",
} as const;

export type NoticeSize = keyof typeof SIZES;

export interface NoticeProps extends React.ComponentProps<"div"> {
  tone?: NoticeTone;
  size?: NoticeSize;
  /** Hide the leading icon when the surrounding layout already signals tone. */
  icon?: boolean;
}

export function Notice({
  tone = "neutral",
  size = "md",
  icon = true,
  className,
  children,
  ...props
}: NoticeProps) {
  const IconCmp = ICONS[tone];
  return (
    <div
      data-slot="notice"
      role={tone === "danger" ? "alert" : tone === "success" ? "status" : undefined}
      className={cn("flex items-start", SIZES[size], TONES[tone], className)}
      {...props}
    >
      {icon && (
        <IconCmp
          data-slot="notice-icon"
          className="mt-0.5 shrink-0"
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
